import { makeAutoObservable, runInAction } from "mobx";
import { formatApiDateTime } from "../../utils/Backend";
import { uploadAppFileWithKey } from "../../global/OssUploadApi";
import { CustomerServiceChatApi } from "./CustomerServiceChatApi";
import type { CsChatChannel, CsMessage } from "./CustomerServiceChatTypes";
import {
  CS_DISPLAY_NAME,
  CS_MAX_VISIBLE_MESSAGES,
  CS_WELCOME_MESSAGE_ID,
  CS_WELCOME_TEXT,
} from "./CustomerServiceChatTypes";

export type CustomerServiceChatModelConfig = {
  channel?: CsChatChannel;
  spaceId?: string;
  displayName?: string;
};

function cmpMsgId(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

function trimToVisibleWindow(messages: CsMessage[]): CsMessage[] {
  if (messages.length <= CS_MAX_VISIBLE_MESSAGES) {
    return messages;
  }
  return messages.slice(messages.length - CS_MAX_VISIBLE_MESSAGES);
}

function stripWelcome(messages: CsMessage[]): CsMessage[] {
  return messages.filter((m) => m.id !== CS_WELCOME_MESSAGE_ID);
}

function createWelcomeMessage(displayName: string): CsMessage {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const sentAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  return {
    id: CS_WELCOME_MESSAGE_ID,
    senderType: "STAFF",
    senderName: displayName,
    content: CS_WELCOME_TEXT,
    messageType: "TEXT",
    sentAt,
  };
}

function withWelcomeIfEmpty(messages: CsMessage[], displayName: string): CsMessage[] {
  return messages.length > 0 ? messages : [createWelcomeMessage(displayName)];
}

function resolveMaxMessageId(messages: CsMessage[]): string | null {
  const real = stripWelcome(messages);
  return real.length ? real[real.length - 1].id : null;
}

function mergeMessages(existing: CsMessage[], incoming: CsMessage[]): CsMessage[] {
  const map = new Map<string, CsMessage>();
  for (const m of existing) {
    map.set(m.id, m);
  }
  for (const m of incoming) {
    map.set(m.id, m);
  }
  return Array.from(map.values()).sort((a, b) => cmpMsgId(a.id, b.id));
}

export class CustomerServiceChatModel {
  conversationId: string | null = null;
  messages: CsMessage[] = [];
  loading = false;
  loadingOlder = false;
  sending = false;
  hasOlder = false;
  historyRecycled = false;
  draft = "";
  error: string | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private maxMessageId: string | null = null;
  private readonly channel: CsChatChannel;
  private readonly spaceId: string;
  private readonly displayName: string;

  constructor(config?: CustomerServiceChatModelConfig) {
    this.channel = config?.channel ?? "PLATFORM";
    this.spaceId = config?.spaceId?.trim() ?? "";
    this.displayName = config?.displayName ?? CS_DISPLAY_NAME;
    makeAutoObservable(this);
  }

  get canSend(): boolean {
    return Boolean(this.conversationId) && !this.sending && this.draft.trim().length > 0;
  }

  setDraft(v: string) {
    this.draft = v;
  }

  async bootstrap() {
    this.loading = true;
    this.error = null;
    try {
      const res = await CustomerServiceChatApi.getOrCreateConversation(
        this.channel,
        this.spaceId || undefined
      );
      if (typeof res === "string") {
        runInAction(() => {
          this.error = "加载失败";
          this.loading = false;
        });
        return;
      }
      runInAction(() => {
        this.conversationId = res.conversationId;
      });
      await this.loadInitialMessages();
      await this.markRead();
      this.startPoll();
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  dispose() {
    this.stopPoll();
  }

  async loadInitialMessages() {
    if (!this.conversationId) return;
    const res = await CustomerServiceChatApi.listMessages(this.conversationId);
    if (typeof res === "string") return;
    runInAction(() => {
      const loaded = trimToVisibleWindow(res.list.map(normalizeMessage));
      this.messages = withWelcomeIfEmpty(loaded, this.displayName);
      this.hasOlder = loaded.length > 0 ? Boolean(res.hasOlder) : false;
      this.historyRecycled = loaded.length > 0 ? Boolean(res.historyRecycled) : false;
      this.maxMessageId = resolveMaxMessageId(loaded);
    });
  }

  async loadOlder() {
    if (!this.conversationId || this.loadingOlder || !this.hasOlder || this.historyRecycled) return;
    if (stripWelcome(this.messages).length >= CS_MAX_VISIBLE_MESSAGES) {
      runInAction(() => {
        this.hasOlder = false;
        this.historyRecycled = true;
      });
      return;
    }
    const firstId = stripWelcome(this.messages)[0]?.id;
    if (!firstId) return;
    this.loadingOlder = true;
    try {
      const res = await CustomerServiceChatApi.listMessages(this.conversationId, firstId);
      if (typeof res === "string") return;
      runInAction(() => {
        const merged = trimToVisibleWindow(
          mergeMessages(res.list.map(normalizeMessage), stripWelcome(this.messages))
        );
        this.messages = withWelcomeIfEmpty(merged, this.displayName);
        this.hasOlder = Boolean(res.hasOlder);
        this.historyRecycled = Boolean(res.historyRecycled);
      });
    } finally {
      runInAction(() => {
        this.loadingOlder = false;
      });
    }
  }

  async send() {
    const text = this.draft.trim();
    if (!this.conversationId || !text || this.sending) return;
    this.sending = true;
    try {
      const res = await CustomerServiceChatApi.sendMessage(this.conversationId, text, "TEXT");
      if (typeof res === "string") return;
      runInAction(() => {
        this.draft = "";
        const merged = mergeMessages(stripWelcome(this.messages), [normalizeMessage(res)]);
        const trimmed = trimToVisibleWindow(merged);
        this.messages = withWelcomeIfEmpty(trimmed, this.displayName);
        if (merged.length > CS_MAX_VISIBLE_MESSAGES) {
          this.hasOlder = false;
          this.historyRecycled = true;
        }
        this.maxMessageId = resolveMaxMessageId(trimmed);
      });
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async sendImage(file: File) {
    if (!this.conversationId || this.sending) return;
    const ext = file.name.split(".").pop()?.trim().toLowerCase() || "jpg";
    const normalizedExt = ext === "jpeg" ? "jpg" : ext;
    this.sending = true;
    try {
      const prep = await CustomerServiceChatApi.prepareImageUpload(this.conversationId, normalizedExt);
      if (typeof prep === "string") return;
      const objectKey = prep.objectKey?.trim();
      if (!objectKey) return;
      const url = await uploadAppFileWithKey(file, objectKey);
      const res = await CustomerServiceChatApi.sendMessage(this.conversationId, url, "IMAGE");
      if (typeof res === "string") return;
      runInAction(() => {
        const merged = mergeMessages(stripWelcome(this.messages), [normalizeMessage(res)]);
        const trimmed = trimToVisibleWindow(merged);
        this.messages = withWelcomeIfEmpty(trimmed, this.displayName);
        this.maxMessageId = resolveMaxMessageId(trimmed);
      });
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async pollOnce() {
    if (!this.conversationId) return;
    const res = await CustomerServiceChatApi.pollNewMessages(
      this.conversationId,
      this.maxMessageId
    );
    if (typeof res === "string" || !res.list?.length) return;
    runInAction(() => {
      const merged = mergeMessages(stripWelcome(this.messages), res.list.map(normalizeMessage));
      const trimmed = trimToVisibleWindow(merged);
      this.messages = withWelcomeIfEmpty(trimmed, this.displayName);
      if (merged.length > CS_MAX_VISIBLE_MESSAGES) {
        this.hasOlder = false;
        this.historyRecycled = true;
      }
      this.maxMessageId = resolveMaxMessageId(trimmed);
    });
  }

  private async markRead() {
    if (!this.conversationId) return;
    await CustomerServiceChatApi.markRead(this.conversationId);
  }

  private startPoll() {
    this.stopPoll();
    this.pollTimer = setInterval(() => {
      void this.pollOnce();
    }, 3000);
  }

  private stopPoll() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}

function normalizeMessage(m: CsMessage): CsMessage {
  return {
    ...m,
    sentAt: formatApiDateTime(m.sentAt),
    senderName: m.senderName,
  };
}
